/**
 * useSignIn - Custom hook for sign in functionality
 * 
 * Handles authentication logic with error handling and loading states.
 * 
 * @example
 * ```tsx
 * const { signIn, isLoading, error } = useSignIn();
 * 
 * await signIn({ email: "user@example.com", password: "password123" });
 * ```
 */

import { useCallback, useState } from "react";
import { signIn } from "next-auth/react";
import { getAuthErrorMessage } from "@/lib/utils/auth/error-messages";

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface UseSignInOptions {
  /**
   * Callback executed on successful sign in
   */
  onSuccess?: () => void;
  /**
   * Callback executed on error
   */
  onError?: (error: string) => void;
}

export interface UseSignInReturn {
  /**
   * Sign in function
   */
  signIn: (credentials: SignInCredentials) => Promise<boolean>;
  /**
   * Loading state
   */
  isLoading: boolean;
  /**
   * Error message (user-friendly)
   */
  error: string | null;
  /**
   * Clear error manually
   */
  clearError: () => void;
}

export function useSignIn(options?: UseSignInOptions): UseSignInReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleSignIn = useCallback(
    async (credentials: SignInCredentials): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await signIn("credentials", {
          redirect: false,
          email: credentials.email,
          password: credentials.password,
        });

        if (result?.error) {
          const friendlyError = getAuthErrorMessage(result.error);
          setError(friendlyError);
          options?.onError?.(friendlyError);
          return false;
        }

        // Success
        options?.onSuccess?.();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unexpected error occurred. Please try again.";
        const friendlyError = getAuthErrorMessage(errorMessage);
        setError(friendlyError);
        options?.onError?.(friendlyError);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [options],
  );

  return {
    signIn: handleSignIn,
    isLoading,
    error,
    clearError,
  };
}

